// Copyright (c) 2026, Orgain and contributors
// For license information, please see license.txt

/**
 * OutgoingEmailSection - the whole "Email" settings tab, redesigned around a
 * single concept: pick the mailbox that sends notifications.
 *
 * One account list (every account is shown, so nothing silently "disappears"),
 * a clear sender selection, live connection status, and a single guided
 * "Add account" entry point. Replaces the old provider-picker +
 * connect-block + hidden-filter dropdown that confused operators.
 */

import { useCallback, useEffect, useState } from 'react';

import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CheckCircle2,
  AlertTriangle,
  Plus,
  Mail,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react';

import {
  brainConfigService,
  type EmailAccountSummary,
  type EmailAccountStatus,
  type EmailProviderPref,
} from '@/services/kbService';
import { logger } from '@/utils/logger';
import { notifySetupStatusChanged } from '@/utils/setupStatus';
import EmailAccountForm from './EmailAccountForm';

interface Props {
  enableNotifications: boolean;
  /** Currently-selected sender account name (or null = system default). */
  emailAccount: string | null;
  onChange: (
    patch: Partial<{
      enable_email_notifications: boolean;
      email_account: string | null;
      email_provider_pref: EmailProviderPref | null;
    }>,
    // `silent` suppresses the failure toast for background writes the user
    // didn't initiate (the auto-default sender pick) - see commitEmail.
    opts?: { silent?: boolean },
  ) => void;
}

const isOutgoing = (a: EmailAccountSummary) => !!a.enable_outgoing;

const prefForService = (service?: string): EmailProviderPref => {
  const s = (service || '').toLowerCase();
  if (s.includes('gmail')) return 'Gmail';
  if (s.includes('outlook')) return 'Outlook';
  return 'IMAP';
};
export default function OutgoingEmailSection({
  enableNotifications,
  emailAccount,
  onChange,
}: Props) {
  const [accounts, setAccounts] = useState<EmailAccountSummary[]>([]);
  const [status, setStatus] = useState<EmailAccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editName, setEditName] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      // All accounts - outgoing AND incoming - so nothing vanishes from view.
      const list = await brainConfigService.listEmailAccounts(false);
      setAccounts(list);
    } catch (err) {
      logger.error('Failed to list email accounts', err);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshStatus = useCallback(async (name: string | null) => {
    if (!name) {
      setStatus(null);
      return;
    }
    setStatusBusy(true);
    try {
      setStatus(await brainConfigService.getEmailAccountStatus(name));
    } catch (err) {
      logger.error('Failed to fetch email account status', err);
      setStatus(null);
    } finally {
      setStatusBusy(false);
    }
  }, []);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);
  useEffect(() => {
    void refreshStatus(emailAccount);
  }, [emailAccount, refreshStatus]);

  // Default the sender to the first outgoing account (preferring the one
  // flagged default-outgoing) when none is selected yet - so there's always
  // an explicit sender and no confusing "nothing selected" state.
  useEffect(() => {
    if (loading || emailAccount) return;
    const list = accounts.filter(isOutgoing);
    if (!list.length) return;
    const pick = list.find((a) => a.default_outgoing) || list[0];
    onChange(
      { email_account: pick.name, email_provider_pref: prefForService(pick.service) },
      { silent: true },
    );
  }, [loading, emailAccount, accounts, onChange]);

  const openAdd = () => {
    setEditName(null);
    setFormOpen(true);
  };
  const openEdit = (name: string) => {
    setEditName(name);
    setFormOpen(true);
  };
  const handleSaved = async (name: string) => {
    await loadAccounts();
    await refreshStatus(name);
    // Newly-added (not editing) accounts become the active sender.
    if (!editName) {
      const saved = await brainConfigService
        .getEmailAccountStatus(name)
        .catch(() => null);
      onChange({
        email_account: name,
        email_provider_pref: prefForService(saved?.service),
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteName) return;
    setDeleting(true);
    try {
      const res = await brainConfigService.deleteEmailAccount(deleteName);
      if (res.ok) {
        toast.success('Email account deleted');
        if (deleteName === emailAccount) onChange({ email_account: null });
        await loadAccounts();
        notifySetupStatusChanged();
      } else {
        toast.error(res.error || 'Could not delete the account');
      }
    } catch (err) {
      logger.error('Delete email account failed', err);
      toast.error('Could not delete the account');
    } finally {
      setDeleting(false);
      setDeleteName(null);
    }
  };

  const sendable = accounts.filter(isOutgoing);

  const renderStatusBadge = (a: EmailAccountSummary) => {
    if (a.name !== emailAccount) return null;
    if (statusBusy) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (status?.oauth_connected) {
      return (
        <Badge variant="secondary">
          <CheckCircle2 className="mr-1 h-3 w-3 text-emerald-400" /> Connected
        </Badge>
      );
    }
    if (status?.exists && status?.awaiting_password) {
      return (
        <Badge variant="secondary">
          <AlertTriangle className="mr-1 h-3 w-3 text-amber-400" /> Awaiting password
        </Badge>
      );
    }
    // An OAuth mailbox that exists but has no usable token has NOT completed
    // authorization and cannot actually send. Show an honest "Not connected"
    // state instead of falling through to a misleading green "Active".
    if (status?.exists && status?.auth_method === 'OAuth' && !status?.oauth_connected) {
      return (
        <Badge variant="secondary">
          <AlertTriangle className="mr-1 h-3 w-3 text-amber-400" /> Not connected - Authorize
        </Badge>
      );
    }
    if (status?.exists) {
      return (
        <Badge variant="secondary">
          <CheckCircle2 className="mr-1 h-3 w-3 text-emerald-400" /> Active
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Connected accounts */}
      <div className="space-y-4">
        {/* No "Add" button once an account exists - the backend enforces a
            single-Email-Account limit (save_email_account throws on a second),
            so adding is only offered from the empty state below. */}
        <Label className="text-lg font-semibold">Connected Accounts</Label>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-8 text-center">
            <Mail className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              No email account connected yet.
            </div>
            <Button type="button" variant="outline" onClick={openAdd}>
              <Plus className="mr-2 h-4 w-4" /> Add account
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((a) => {
              const outgoing = isOutgoing(a);
              return (
                <div
                  key={a.name}
                  className={`flex flex-col gap-2 rounded-lg border border-border bg-secondary/20 p-4 sm:flex-row sm:items-center sm:gap-4 ${
                    outgoing ? '' : 'border-dashed'
                  }`}
                >
                  {/* Identity + status - full width on mobile, grows on desktop */}
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary/60 border border-border">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-semibold">
                        {a.email_id || a.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {a.service || 'Email'}
                        {!outgoing && ' · sending disabled'}
                      </div>
                    </div>
                    {outgoing ? (
                      renderStatusBadge(a)
                    ) : (
                      <Badge variant="outline" className="shrink-0">
                        Disabled
                      </Badge>
                    )}
                  </div>
                  {/* Edit / delete - drop to their own row on mobile,
                      right-aligned; stay inline on desktop. */}
                  <div className="flex items-center gap-1 self-end sm:self-auto">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(a.name)}
                      aria-label="Edit account"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteName(a.name)}
                      aria-label="Delete account"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Notifications toggle - useless without a sending-enabled account, so
          it's disabled (and forced off) until one exists. */}
      <div className="flex items-center justify-between gap-4 border-t border-border pt-6">
        <div>
          <Label className="text-base font-semibold">Send email notifications</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Delivers operational alerts covering system errors, usage limits,
            and scheduled reports, and notifies document owners once their
            uploads have been processed.
          </p>
          {!loading && sendable.length === 0 && (
            <p className="text-[11px] text-amber-400 mt-1">
              Add an account with sending enabled to turn this on.
            </p>
          )}
        </div>
        <Switch
          checked={enableNotifications && sendable.length > 0}
          disabled={sendable.length === 0}
          onCheckedChange={(c) => onChange({ enable_email_notifications: c })}
        />
      </div>

      <EmailAccountForm
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          // Always re-pull the list when the dialog closes so a newly added
          // (or just-authorized) account is reflected without a page reload.
          if (!o) {
            void loadAccounts();
            void refreshStatus(emailAccount);
          }
        }}
        accountName={editName}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!deleteName} onOpenChange={(o) => !o && setDeleteName(null)}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete email account?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deletes <span className="font-medium">{deleteName}</span>.
              Email notifications will stop until you add another account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
